import { atom } from 'jotai';

export interface UndoEntryItem {
  dealItemId: string;
  previousHistoryId: string;
  currentHistoryId: string;
}

export interface UndoEntry {
  items: UndoEntryItem[];
}

/**
 * Pile LIFO des opérations annulables pour le CTRL+Z.
 * Initialisée vide — aucune opération n'est annulable à l'ouverture.
 */
export const undoStackState = atom<UndoEntry[]>([]);

/**
 * Pile LIFO des opérations « refaisables » pour le CTRL+Y.
 * Vidée dès qu'une nouvelle mutation utilisateur est enregistrée (comportement éditeur standard).
 */
export const redoStackState = atom<UndoEntry[]>([]);

/** True pendant la mutation `restoreDealItemsHistory` (toutes instances de `useUndoDealItems`). */
export const undoMutationPendingState = atom(false);

export const undoInvalidationPausedState = atom(0);

/**
 * Une entrée est encore cohérente si, pour chaque item présent dans la carte
 * serveur, le currentHistoryId correspond, ou si l'item est encore à son
 * previousHistoryId (modification pas encore relue). Absence dans la carte → pas
 * de conclusion.
 */
export function isUndoEntryConsistentWithHistoryMap(
  entry: UndoEntry,
  currentHistoryIds: Map<string, string>
): boolean {
  return entry.items.every((item) => {
    const serverHistoryId = currentHistoryIds.get(item.dealItemId);
    if (serverHistoryId === undefined) return true;
    return (
      serverHistoryId === item.currentHistoryId ||
      serverHistoryId === item.previousHistoryId
    );
  });
}

/**
 * Si le sommet n’est plus cohérent avec les historyId serveur, on vide toute la
 * pile — même comportement qu’après `restoreConflict` sur restoreDealItemsHistory.
 * Fonctionne pour la pile undo comme pour la pile redo.
 */
export function flushStackIfTopInvalid(
  stack: UndoEntry[],
  currentHistoryIds: Map<string, string>
): UndoEntry[] {
  if (stack.length === 0) return stack;
  const top = stack[stack.length - 1];
  if (isUndoEntryConsistentWithHistoryMap(top, currentHistoryIds)) {
    return stack;
  }
  return [];
}

/** Inverse les previousHistoryId / currentHistoryId d’une entrée (undo ↔ redo). */
export function swapEntry(entry: UndoEntry): UndoEntry {
  return {
    items: entry.items.map((item) => ({
      dealItemId: item.dealItemId,
      previousHistoryId: item.currentHistoryId,
      currentHistoryId: item.previousHistoryId,
    })),
  };
}
